<template>
<div class="modal is-large is-active">
    <div class="modal-background"></div>
    <div class="modal-content">
        <div class="custom-modal-box">
            <div class="default-settings-title">
                <span class="account-name">Add Storage</span>
                <span class="default-settings-subtitle">Connect a new storage provider to have more space for your backups.</span>
            </div>
            <!-- <div class="default-settings-description">
                In order to connect with a new account, select one of the providers below.
            </div> -->

            <div class="backup">
                <div class="columns is-multiline">
                    <div @click="selectProvider('aws_s3')" class="column is-3">
                        <div class="backup-frequency backup-frequency-selectable" :class="{'backup-frequency-selected': providerIsSelected('aws_s3')}">
                            <div class="icon">
                                <font-awesome-icon :icon="['fab', 'aws']"/>
                            </div>
                            <div class="details">
                                <div class="detail-label">
                                    Provider
                                </div>
                                <div class="detail-value">
                                    AWS S3
                                </div>
                            </diV>
                        </div>
                    </div>
                    <div @click="selectProvider('wasabi_s3')" class="column is-3">
                        <div class="backup-retention backup-retention-selectable" :class="{'backup-frequency-selected': providerIsSelected('wasabi_s3')}">
                            <div class="icon">
                                <font-awesome-icon icon="cloud"/>
                            </div>
                            <div class="details">
                                <div class="detail-label">
                                    Provider
                                </div>
                                <div class="detail-value">
                                    Wasabi
                                </div>
                            </diV>
                        </div>
                    </div>
                    <div @click="selectProvider('digitalocean_spaces')" class="column is-3">
                        <div class="backup-retention backup-retention-selectable" :class="{'backup-frequency-selected': providerIsSelected('digitalocean_spaces')}">
                            <div class="icon">
                                <font-awesome-icon :icon="['fab', 'digital-ocean']"/>
                            </div>
                            <div class="details">
                                <div class="detail-label">
                                    Provider
                                </div>
                                <div class="detail-value">
                                    DigitalOcean Spaces
                                </div>
                            </diV>
                        </div>
                    </div>
                    <div @click="selectProvider('backblaze_b2_s3')" class="column is-3">
                        <div class="backup-retention backup-retention-selectable" :class="{'backup-frequency-selected': providerIsSelected('backblaze_b2_s3')}">
                            <div class="icon">
                                <font-awesome-icon icon="cloud"/>
                            </div>
                            <div class="details">
                                <div class="detail-label">
                                    Provider
                                </div>
                                <div class="detail-value">
                                    Backblaze B2
                                </div>
                            </diV>
                        </div>
                    </div>
                    <div @click="selectProvider('azureblob')" class="column is-3">
                        <div class="backup-retention backup-retention-selectable" :class="{'backup-frequency-selected': providerIsSelected('azureblob')}">
                            <div class="icon">
                                <font-awesome-icon :icon="['fab', 'windows']"/>
                            </div>
                            <div class="details">
                                <div class="detail-label">
                                    Provider
                                </div>
                                <div class="detail-value">
                                    Azure Blob Storage
                                </div>
                            </diV>
                        </div>
                    </div>
                    <div @click="selectProvider('sftp')" class="column is-3">
                        <div class="backup-retention backup-retention-selectable" :class="{'backup-frequency-selected': providerIsSelected('sftp')}">
                            <div class="icon">
                                <font-awesome-icon :icon="['fas', 'server']"/>
                            </div>
                            <div class="details">
                                <div class="detail-label">
                                    Provider
                                </div>
                                <div class="detail-value">
                                    SFTP
                                </div>
                            </diV>
                        </div>
                    </div>
                </div>

                <div v-if="providerIsS3Based" class="columns is-multiline">
                    <div class="column is-6">
                        <div class="field">
                            <input v-model="name" @keyup.enter="createStorage" type="text" :disabled="creatingStorage" :class="{ 'is-danger': hasError('name') }" class="input custom-input-size is-medium" placeholder="Friendly name" autocomplete="off"/>
                            <span v-if="!hasError('name')" class="help">A name to recognize the account later</span>
                            <span v-if="hasError('name')" class="help is-danger">{{ getError('name') }}</span>
                        </div>
                    </div>
                    <div class="column is-6">
                        <div class="field">
                            <input v-model="bucketName" @keyup.enter="createStorage" type="text" :disabled="creatingStorage" :class="{ 'is-danger': hasError('bucket_name') }" class="input custom-input-size is-medium" placeholder="Bucket name" autocomplete="off"/>
                            <span v-if="!hasError('bucket_name')" class="help">In this bucket the backups will be stored</span>
                            <span v-if="hasError('bucket_name')" class="help is-danger">{{ getError('bucket_name') }}</span>
                        </div>
                    </div>
                    <div class="column is-6">
                        <div class="field">
                            <input v-model="accessKey" @keyup.enter="createStorage" type="text" :disabled="creatingStorage" :class="{ 'is-danger': hasError('access_key') }" class="input custom-input-size is-medium" placeholder="Access Key ID" autocomplete="off"/>
                            <span v-if="!hasError('access_key')" class="help">Access Key ID</span>
                            <span v-if="hasError('access_key')" class="help is-danger">{{ getError('access_key') }}</span>
                        </div>
                    </div>
                    <div class="column is-6">
                        <div class="field">
                            <input v-model="secretKey" @keyup.enter="createStorage" type="text" :disabled="creatingStorage" :class="{ 'is-danger': hasError('access_secret') }" class="input custom-input-size is-medium" placeholder="Access Key Secret" autocomplete="off"/>
                            <span v-if="!hasError('access_secret')" class="help">Access Key Secret</span>
                            <span v-if="hasError('access_secret')" class="help is-danger">{{ getError('access_secret') }}</span>
                        </div>
                    </div>
                    <div class="column is-6">
                        <div class="field">
                            <div class="control has-icons-left">
                                <div class="select is-fullwidth">
                                    <select v-model="region">
                                        <option v-for="region in regions" v-bind:key="region.slug" :value="region.slug">{{ region.name + ' - ' + region.slug }}</option>
                                    </select>
                                </div>
                                <div class="icon is-left is-custom-icon-left">
                                    <font-awesome-icon icon="history"/>
                                </div>
                            </div>
                        </div>
                        <span class="help" style="margin-top: 19px !important;">Select the region.</span>
                    </div>
                </div>
                <div v-else-if="providerIsAzureblob" class="columns is-multiline">
                    <div class="column is-6">
                        <div class="field">
                            <input v-model="name" @keyup.enter="createStorage" type="text" :disabled="creatingStorage" :class="{ 'is-danger': hasError('name') }" class="input custom-input-size is-medium" placeholder="Friendly name" autocomplete="off"/>
                            <span v-if="!hasError('name')" class="help">A name to easily recognize the storage account later.</span>
                            <span v-if="hasError('name')" class="help is-danger">{{ getError('name') }}</span>
                        </div>
                    </div>
                    <div class="column is-6">
                        <div class="field">
                            <input v-model="azureblobAccount" @keyup.enter="createStorage" type="text" :disabled="creatingStorage" :class="{ 'is-danger': hasError('azureblob_account') }" class="input custom-input-size is-medium" placeholder="Azure account" autocomplete="off"/>
                            <span v-if="!hasError('azureblob_account')" class="help">The name of the Azure Blob account.</span>
                            <span v-if="hasError('azureblob_account')" class="help is-danger">{{ getError('azureblob_account') }}</span>
                        </div>
                    </div>
                    <div class="column is-6">
                        <div class="field">
                            <input v-model="azureblobAccessKey" @keyup.enter="createStorage" type="text" :disabled="creatingStorage" :class="{ 'is-danger': hasError('azureblob_access_key') }" class="input custom-input-size is-medium" placeholder="Azure Access Key" autocomplete="off"/>
                            <span v-if="!hasError('azureblob_access_key')" class="help">Azure Blob Access Key</span>
                            <span v-if="hasError('azureblob_access_key')" class="help is-danger">{{ getError('azureblob_access_key') }}</span>
                        </div>
                    </div>
                    <div class="column is-6">
                        <div class="field">
                            <input v-model="azureblobContainer" @keyup.enter="createStorage" type="text" :disabled="creatingStorage" :class="{ 'is-danger': hasError('azureblob_container') }" class="input custom-input-size is-medium" placeholder="Azure Blob Container" autocomplete="off"/>
                            <span v-if="!hasError('azureblob_container')" class="help">Azure Blob Container</span>
                            <span v-if="hasError('azureblob_container')" class="help is-danger">{{ getError('azureblob_container') }}</span>
                        </div>
                    </div>
                </div>
                <div v-else-if="providerIsSftp" class="columns is-multiline">
                    <div class="column is-6">
                        <div class="field">
                            <input v-model="name" @keyup.enter="createStorage" type="text" :disabled="creatingStorage" :class="{ 'is-danger': hasError('name') }" class="input custom-input-size is-medium" placeholder="Friendly name" autocomplete="off"/>
                            <span v-if="!hasError('name')" class="help">A name to easily recognize the storage later.</span>
                            <span v-if="hasError('name')" class="help is-danger">{{ getError('name') }}</span>
                        </div>
                    </div>
                    <div class="column is-6">
                        <div class="field">
                            <input v-model="sftpHost" @keyup.enter="createStorage" type="text" :disabled="creatingStorage" :class="{ 'is-danger': hasError('sftp_host') }" class="input custom-input-size is-medium" placeholder="SFTP host" autocomplete="off"/>
                            <span v-if="!hasError('sftp_host')" class="help">The host of the SFTP server.</span>
                            <span v-if="hasError('sftp_host')" class="help is-danger">{{ getError('sftp_host') }}</span>
                        </div>
                    </div>
                    <div class="column is-6">
                        <div class="field">
                            <input v-model="sftpUsername" @keyup.enter="createStorage" type="text" :disabled="creatingStorage" :class="{ 'is-danger': hasError('sftp_username') }" class="input custom-input-size is-medium" placeholder="SFTP user" autocomplete="off"/>
                            <span v-if="!hasError('sftp_username')" class="help">The user of the SFTP server.</span>
                            <span v-if="hasError('sftp_username')" class="help is-danger">{{ getError('sftp_username') }}</span>
                        </div>
                    </div>
                    <div class="column is-6">
                        <div class="field">
                            <input v-model="sftpPassword" @keyup.enter="createStorage" type="text" :disabled="creatingStorage" :class="{ 'is-danger': hasError('sftp_password') }" class="input custom-input-size is-medium" placeholder="SFTP password" autocomplete="off"/>
                            <span v-if="!hasError('sftp_password')" class="help">The password of the SFTP server.</span>
                            <span v-if="hasError('sftp_password')" class="help is-danger">{{ getError('sftp_password') }}</span>
                        </div>
                    </div>
                    <div class="column is-6">
                        <div class="field">
                            <input v-model="sftpPort" @keyup.enter="createStorage" type="text" :disabled="creatingStorage" :class="{ 'is-danger': hasError('sftp_port') }" class="input custom-input-size is-medium" placeholder="SFTP port" autocomplete="off"/>
                            <span v-if="!hasError('sftp_port')" class="help">The port of the SFTP server.</span>
                            <span v-if="hasError('sftp_port')" class="help is-danger">{{ getError('sftp_port') }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="columns is-multiline" style="margin-top: 10px;">
                <div class="column is-3">
                    <div @click="createStorage" :disabled="creatingStorage" :class="{ 'is-loading': creatingStorage }" class="button custom-button-size is-fullwidth is-info">Connect Storage</div>
                </div>
                <div class="column is-3">
                    <div @click="close" class="close-add-monitor-modal button custom-button-size is-fullwidth is-danger" :disabled="creatingStorage">Cancel</div>
                </div>
            </div>
        </div>
    </div>
    <button @click="close" class="modal-close is-large" aria-label="close"></button>
</div>
</template>

<script>
import Api from '@/api';
import FormErrorsMixin from '@/mixins/FormErrors';

export default {
    mixins: [FormErrorsMixin],

    data() {
        return {
            allowedInputs: [
                'name', 'access_key', 'secret_key', 'bucket_name',
                'azureblob_account', 'azureblob_access_key', 'azureblob_container',
                'sftp_host', 'sftp_password', 'sftp_username', 'sftp_port',
            ],
            creatingStorage: false,
            selectedProviderSlug: 'aws_s3',
            name: '',
            accessKey: '',
            secretKey: '',
            bucketName: '',
            region: '',
            regions: [],
            azureblobAccount: '',
            azureblobAccessKey: '',
            azureblobContainer: '',
            sftpHost: '',
            sftpUsername: '',
            sftpPort: 22,
            sftpPassword: '',
        }
    },

    mounted() {
        this.getProviderRegions();
        this.listenEvents();
    },

    methods: {
        close() {
            if (!this.creatingStorage) {
                Event.$emit('addStorageModalClosed');
            }
        },

        getProviderRegions() {
            Api().get('storages/' + this.selectedProviderSlug + '/regions')
                .then((success) => {
                    this.regions = success.data.regions;
                    this.region = this.regions[0].slug;
                });
        },

        createStorage() {
            if (this.creatingStorage) {
                return;
            }

            this.creatingStorage = true;
            this.resetErrors();

            Api().post('storages', this.storageData)
                .then((success) => {
                    Event.$emit('refreshStorages', () => {
                        this.close();
                        swal('Great!', 'The new storage was added.', 'success');
                    });
                })
                .catch((error) => {
                    if (!this.storeErrors(error.response.data.errors)) {
                        if (typeof error.response.data.message == 'string') {
                            this.error = error.response.data.message;
                        } else {
                            this.error = 'Something wrong happened. Refresh the page and try again.';
                        }
                    }
                })
                .finally(() => {
                    this.creatingStorage = false;
                });
        },

        listenEvents() {
            var vm = this;

            Event.$on('showAddStorageModal', () => {
                vm.active = true;
            });
        },

        providerIsSelected(providerSlug) {
            return this.selectedProviderSlug === providerSlug;
        },

        selectProvider(providerSlug) {
            this.selectedProviderSlug = providerSlug;
        },
    },

    computed: {
        storageData() {
            if (this.providerIsS3Based) {
                return {
                    name: this.name,
                    provider: this.selectedProviderSlug,
                    region: this.region,
                    access_key: this.accessKey,
                    secret_key: this.secretKey,
                    bucket_name: this.bucketName,
                };
            }

            if (this.providerIsAzureblob) {
                return {
                    name: this.name,
                    provider: this.selectedProviderSlug,
                    azureblob_account: this.azureblobAccount,
                    azureblob_access_key: this.azureblobAccessKey,
                    azureblob_container: this.azureblobContainer,
                };
            }

            if (this.providerIsSftp) {
                return {
                    name: this.name,
                    provider: this.selectedProviderSlug,
                    sftp_host: this.sftpHost,
                    sftp_username: this.sftpUsername,
                    sftp_port: this.sftpPort,
                    sftp_password: this.sftpPassword,
                };
            }

            return {};
        },

        providerIsS3Based() {
            return ['aws_s3', 'digitalocean_spaces', 'wasabi_s3', 'backblaze_b2_s3'].includes(this.selectedProviderSlug);
        },

        providerIsAzureblob() {
            return this.selectedProviderSlug == 'azureblob';
        },

        providerIsSftp() {
            return this.selectedProviderSlug == 'sftp';
        }
    },

    watch: {
        selectedProviderSlug() {
            if (this.providerIsS3Based) {
                this.getProviderRegions();
            }
        },
    },
}
</script>
